/**
 * Google Reviews Service
 * Fetches Google reviews and ratings for hotels using Google Places API
 */

import { loadGoogleMapsScript } from "../utils/googleMapsLoader";
import { getCachedData, setCachedData } from "./cacheService";

// Cache TTL: 60 days
const CACHE_TTL = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds

// Cache version - increment this to invalidate old cache
// Version 4: Clear cache to force fresh data
const CACHE_VERSION = 4;

// Helper function to clear old cache versions
const clearOldCacheVersions = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      // Clear all old versions of google_reviews cache
      if (key.startsWith('hr_cache_google_reviews_v') && !key.startsWith(`hr_cache_google_reviews_v${CACHE_VERSION}_`)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing old cache:', error);
  }
};

// Clear old cache on load
if (typeof window !== 'undefined') {
  clearOldCacheVersions();
}

// Helper function to create cache key
const getCacheKey = (hotelName, city) => {
  return `google_reviews_v${CACHE_VERSION}_${hotelName.toLowerCase().replace(/\s+/g, '_')}_${city?.toLowerCase().replace(/\s+/g, '_') || 'unknown'}`;
};

/**
 * Find hotel place_id by name and city
 * @param {string} hotelName - Name of the hotel
 * @param {string} city - City name
 * @returns {Promise<string|null>} - Place ID or null if not found
 */
const findHotelPlaceId = async (hotelName, city) => {
  try {
    // DISABLED - Blocked to prevent Google Maps Places API calls
    return null;
    
    // Check cache first
    const cacheKey = getCacheKey(hotelName, city);
    const cachedPlaceId = getCachedData(`${cacheKey}_place_id`, CACHE_TTL);
    if (cachedPlaceId) {
      return cachedPlaceId;
    }

    // Load Google Maps script
    await loadGoogleMapsScript({
      libraries: ["places"],
      language: "he",
    });

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      throw new Error("Google Maps API לא נטען");
    }

    // Create temporary map element
    const mapDiv = document.createElement("div");
    mapDiv.style.width = "1px";
    mapDiv.style.height = "1px";
    mapDiv.style.position = "absolute";
    mapDiv.style.left = "-9999px";
    document.body.appendChild(mapDiv);

    const map = new window.google.maps.Map(mapDiv, {
      center: { lat: 0, lng: 0 },
      zoom: 1,
    });

    const service = new window.google.maps.places.PlacesService(map);

    // Search for hotel
    const query = `${hotelName} ${city}`;
    const request = {
      query: query,
      fields: ["place_id", "name", "rating", "user_ratings_total"],
    };

    return new Promise((resolve) => {
      service.textSearch(request, (results, status) => {
        // Clean up
        if (mapDiv && mapDiv.parentNode) {
          document.body.removeChild(mapDiv);
        }

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results &&
          results.length > 0
        ) {
          // Find best match (exact or close match)
          const bestMatch = results.find(
            (place) =>
              place.name.toLowerCase().includes(hotelName.toLowerCase()) ||
              hotelName.toLowerCase().includes(place.name.toLowerCase())
          ) || results[0];

          const placeId = bestMatch.place_id;
          // Cache place_id
          setCachedData(`${cacheKey}_place_id`, placeId, CACHE_TTL);
          resolve(placeId);
        } else {
          resolve(null);
        }
      });
    });
  } catch (err) {
    console.error("Error finding hotel place_id:", err);
    return null;
  }
};

/**
 * Get hotel reviews from Google Places API
 * @param {string} hotelName - Name of the hotel
 * @param {string} city - City name
 * @param {string} address - Optional address for better matching
 * @returns {Promise<object|null>} - Reviews data or null
 */
export const getHotelGoogleReviews = async (hotelName, city, address = null) => {
  // DISABLED - Blocked to prevent Google Maps Places API calls
  return null;
  
  if (!hotelName || !city) {
    return null;
  }

  // Check cache first
  const cacheKey = getCacheKey(hotelName, city);
  const cachedReviews = getCachedData(cacheKey, CACHE_TTL);
  if (cachedReviews) {
    return cachedReviews;
  }

  try {
    // First, find the place_id
    const placeId = await findHotelPlaceId(hotelName, city);
    if (!placeId) {
      // Cache empty result
      setCachedData(cacheKey, null, CACHE_TTL);
      return null;
    }

    // Load Google Maps script
    await loadGoogleMapsScript({
      libraries: ["places"],
      language: "he",
    });

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      throw new Error("Google Maps API לא נטען");
    }

    // Create temporary map element
    const mapDiv = document.createElement("div");
    mapDiv.style.width = "1px";
    mapDiv.style.height = "1px";
    mapDiv.style.position = "absolute";
    mapDiv.style.left = "-9999px";
    document.body.appendChild(mapDiv);

    const map = new window.google.maps.Map(mapDiv, {
      center: { lat: 0, lng: 0 },
      zoom: 1,
    });

    const service = new window.google.maps.places.PlacesService(map);

    // Get place details with reviews
    const request = {
      placeId: placeId,
      fields: [
        "name",
        "rating",
        "user_ratings_total",
        "reviews",
        "formatted_address",
        "place_id",
      ],
    };

    return new Promise((resolve) => {
      service.getDetails(request, (place, status) => {
        // Clean up
        if (mapDiv && mapDiv.parentNode) {
          document.body.removeChild(mapDiv);
        }

        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          // Google Places API returns up to 5 reviews by default
          // We'll take all available reviews and sort them
          const allReviews = (place.reviews || [])
            .map((review) => ({
              author: review.author_name || "אנונימי",
              rating: review.rating || 0,
              text: review.text || "",
              time: review.time || null,
              relativeTime: review.relative_time_description || "",
            }))
            .sort((a, b) => {
              // Sort by time (most recent first)
              // time is in seconds since epoch
              const timeA = a.time || 0;
              const timeB = b.time || 0;
              return timeB - timeA; // Descending order (newest first)
            });
          
          
          // Note: Google Places API only returns up to 5 reviews
          // To get more, we would need to use Google My Business API
          // For now, we'll use all available reviews (up to 5)

          const reviewsData = {
            rating: place.rating || 0,
            ratingsCount: place.user_ratings_total || 0,
            reviews: allReviews,
            placeId: place.place_id,
            address: place.formatted_address || address || "",
          };

          // Cache the result
          setCachedData(cacheKey, reviewsData, CACHE_TTL);
          resolve(reviewsData);
        } else {
          // Cache empty result
          setCachedData(cacheKey, null, CACHE_TTL);
          resolve(null);
        }
      });
    });
  } catch (err) {
    console.error("Error fetching Google reviews:", err);
    return null;
  }
};

